import type { UseQueryResult } from "@tanstack/react-query";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { Ago, FetchingDot, SectionCard } from "../../components/primitives";
import { formatClock, formatCount, formatMs, formatPercent } from "../../format";
import { useMonitoring } from "../../monitoring-context";
import type { LiveSection, MetricSeries } from "../../types";
import { QueryBody, StatTile } from "./bits";
import { coveredMinutes, RECENT_RING_SIZE, SLOW_REQUEST_MS, splitRoute } from "./utils";

/** "2xx 120 · 3xx 0 · 4xx 3 · 5xx 1": 4xx amber and 5xx red when there are any */
export function ClassBreakdown({ counts }: { counts: { s2: number; s3: number; s4: number; s5: number } }) {
  const items = [
    { key: "s2", label: "2xx", value: counts.s2, cls: "" },
    { key: "s3", label: "3xx", value: counts.s3, cls: "" },
    { key: "s4", label: "4xx", value: counts.s4, cls: counts.s4 > 0 ? "text-amber-300" : "" },
    { key: "s5", label: "5xx", value: counts.s5, cls: counts.s5 > 0 ? "text-red-300" : "" },
  ];
  return (
    <span className="inline-flex flex-wrap gap-x-2 gap-y-0.5">
      {items.map((item) => (
        <span key={item.key} className="whitespace-nowrap">
          {item.label} <span className={cn("font-mono tabular-nums text-foreground/80", item.cls)}>{formatCount(item.value)}</span>
        </span>
      ))}
    </span>
  );
}

/** What the answering instance is doing right now (its in-memory request ring). */
export function LiveTraffic({ live }: { live: UseQueryResult<LiveSection> }) {
  const { t } = useLocalization();
  const instance = live.data?.runtime.process.instance;
  return (
    <SectionCard
      title={t("monitoring.requests.live.title")}
      description={instance ? t("monitoring.requests.live.description", { instance }) : undefined}
      actions={<FetchingDot active={live.isFetching} />}
    >
      <QueryBody query={live} height={96}>
        {(data) => {
          const http = data.http;
          const minute = http.last_minute;
          const capped = minute.n >= RECENT_RING_SIZE;
          const lastRequest = http.recent[0];
          const lastRoute = lastRequest ? splitRoute(`${lastRequest.method} ${lastRequest.path}`) : null;
          return (
            <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
              <StatTile
                label={t("monitoring.requests.live.lastMinute")}
                value={capped ? `≥ ${formatCount(minute.n)}` : formatCount(minute.n)}
                hint={capped ? t("monitoring.requests.live.capped", { size: RECENT_RING_SIZE }) : undefined}
                tone={minute.s5 > 0 ? "nok" : "neutral"}
                detail={<ClassBreakdown counts={minute} />}
              />
              <StatTile
                label={t("monitoring.requests.live.inFlight")}
                value={formatCount(http.in_flight)}
                detail={t("monitoring.requests.live.inFlightDetail")}
              />
              <StatTile
                label={t("monitoring.requests.live.lastRequest")}
                value={lastRequest ? <Ago ts={lastRequest.ts} /> : "–"}
                detail={
                  lastRequest && lastRoute ? (
                    <span className="break-all font-mono">
                      {formatClock(lastRequest.ts)} · {lastRoute.method} {lastRoute.path} · {lastRequest.status}
                    </span>
                  ) : (
                    t("monitoring.requests.live.noRequest")
                  )
                }
              />
              <StatTile
                label={t("monitoring.requests.live.sinceStart")}
                value={formatCount(http.started_total)}
                detail={
                  <>
                    {t("monitoring.requests.live.startedAt")} <Ago ts={data.runtime.process.started_at} />
                  </>
                }
              />
            </div>
          );
        }}
      </QueryBody>
    </SectionCard>
  );
}

/** Totals of the board window from the persisted metrics (scope of the board). */
export function WindowTraffic({ metrics }: { metrics: UseQueryResult<MetricSeries> }) {
  const { t, formatNumber } = useLocalization();
  const { window } = useMonitoring();
  return (
    <SectionCard
      title={t("monitoring.requests.window.title", { window: t(`monitoring.window.long.${window}`) })}
      description={t("monitoring.requests.window.description")}
      actions={<FetchingDot active={metrics.isFetching} />}
    >
      <QueryBody query={metrics} height={96}>
        {(data) => {
          const counters = data.totals.counters;
          const n = counters["http.n"] ?? 0;
          const s4 = counters["http.s4"] ?? 0;
          const s5 = counters["http.s5"] ?? 0;
          const exceptions = counters["event.http.unhandled_exception"] ?? 0;
          const latency = data.totals.hists["http.latency_ms"];
          const maxInFlight = data.totals.maxes["http.in_flight"];
          const minutes = coveredMinutes(data, metrics.dataUpdatedAt);
          const share = (count: number) => formatPercent(n ? count / n : null, count && n && count / n < 0.01 ? 2 : 1);
          return (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 xl:grid-cols-5">
              <StatTile
                label={t("monitoring.requests.window.requests")}
                value={formatCount(n)}
                detail={t("monitoring.requests.perMinute", {
                  rate: formatNumber(n / minutes, { maximumFractionDigits: n / minutes < 10 ? 1 : 0 }),
                })}
              />
              <StatTile
                label={t("monitoring.requests.window.serverErrors")}
                value={formatCount(s5)}
                tone={s5 > 0 ? "nok" : "neutral"}
                detail={
                  <>
                    {t("monitoring.requests.window.share", { share: share(s5) })}
                    {exceptions > 0 && <> · {t("monitoring.requests.window.exceptions", { count: formatCount(exceptions) })}</>}
                  </>
                }
              />
              <StatTile
                label={t("monitoring.requests.window.clientErrors")}
                value={formatCount(s4)}
                tone={s4 > 0 ? "warn" : "neutral"}
                detail={t("monitoring.requests.window.share", { share: share(s4) })}
              />
              <StatTile
                label={t("monitoring.requests.window.latencyP95")}
                value={formatMs(latency?.p95)}
                tone={(latency?.p95 ?? 0) > SLOW_REQUEST_MS ? "warn" : "neutral"}
                hint={t("monitoring.requests.latencyExcludes")}
                detail={t("monitoring.requests.window.latencyDetail", {
                  p50: formatMs(latency?.p50),
                  p99: formatMs(latency?.p99),
                  max: formatMs(latency?.max),
                })}
              />
              <StatTile
                label={t("monitoring.requests.window.maxInFlight")}
                value={formatCount(maxInFlight)}
                detail={t("monitoring.requests.window.maxInFlightDetail")}
                className="col-span-2 sm:col-span-1"
              />
            </div>
          );
        }}
      </QueryBody>
    </SectionCard>
  );
}
